import { useMemo } from 'react';
import {
  Draggable,
} from '@hello-pangea/dnd';

import {
  ExerciseProperty,
  WorkoutExercise as WorkoutExerciseType,
} from '@gymcloud/core';

import {
  Icon,
  Tooltip,
  Dropdown,
  Checkbox,
  ExerciseProperties,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import ExerciseOrder from './ExerciseOrder';
import Alternatives from './Alternatives';

import * as S from './styled';

type Props = {
  index: number,
  readOnly?: boolean,
  exercise: WorkoutExerciseType,
  number: number,
  numbers: number[],
  properties: ExerciseProperty[],
  prevNumber: number,
  nextNumber: number,
  canDieRoll: boolean,
  visibleSelect: boolean,

  onSelect: { (exerciseId: number, selected: boolean): void },
  onRequestUpdate: { (exerciseId: number, data: any): void },
  onRequestDelete: { (exercise: WorkoutExerciseType): void },
  onRequestReplace: { (exercise: WorkoutExerciseType): void },
  onToggleDieRoll: { (exercise: WorkoutExerciseType): void },
  onRequestDieRoll: { (exercise: any): void },
  onRequestSetOrderName: { (exerciseId: number, newNumber: number): void },

  onRequestAddProperty: { (data: any): void },
  onRequestSaveProperty: { (
    propertyId: number,
    payload: any,
    isNew: boolean,
  ): void },
  onRequestDeleteProperty: { (propertyId: number, isNew: boolean): void },
  onRequestAddAlternative: { (exerciseId: number): void },
  onRequestSetAlternative: { (exerciseId: number, alternativeId: number): void },
  onRequestRemoveAlternative: { (alternative: WorkoutExerciseType): void },
};

const tooltipText = {
  exerciseProperty: 'Hover over exercises and click this button to start adding exercise properties.',
};

const WorkoutExercise = ({
  index,
  number,
  numbers,
  exercise,
  readOnly = false,
  nextNumber,
  prevNumber,
  properties,
  canDieRoll,
  visibleSelect,

  onSelect,
  onRequestUpdate,
  onRequestDelete,
  onRequestReplace,
  onToggleDieRoll,
  onRequestDieRoll,
  onRequestSetOrderName,

  onRequestAddProperty,
  onRequestSaveProperty,
  onRequestDeleteProperty,
  onRequestAddAlternative,
  onRequestSetAlternative,
  onRequestRemoveAlternative,
}: Props) => {
  const actions = useMemo(() => {
    const special = [];
    if (canDieRoll) {
      special.push({
        name: exercise.hasDieRoll ? 'Remove die roll' : 'Add die roll',
        divider: true,
        onClick: () => onToggleDieRoll(exercise),
      });
    }

    return [
      ...special,
      {
        name: 'Add alternative',
        onClick: () => onRequestAddAlternative(exercise.id),
      },
      {
        name: 'Replace',
        onClick: () => onRequestReplace(exercise),
      },
      {
        name: 'Delete',
        color: '#e54d42',
        onClick: () => onRequestDelete(exercise),
      },
    ];
  }, [canDieRoll, exercise]);

  return readOnly
    ? (
      <S.WorkoutExercise
        readOnly
        number={number}
        topLine={number === prevNumber}
        bottomLine={number === nextNumber}
      >
        <S.Meta>
          <ExerciseOrder
            readOnly
            number={number}
            numbers={numbers}
            exercise={exercise}
            onChange={onRequestSetOrderName}
          />
          <Flex column>
            <Flex>
              <S.Name
                to={`/workout_exercises/${exercise.id}/overview`}
              >
                {exercise.name}
              </S.Name>
              <Alternatives
                readOnly
                exercise={exercise}
              />
            </Flex>
            <Text
              size="12px"
              color="#282d30"
              whiteSpace="pre-wrap"
            >
              {exercise.note || ''}
            </Text>
          </Flex>
        </S.Meta>
        <S.PropertyGroup>
          <ExerciseProperties
            readOnly
            exerciseId={exercise.id}
            properties={properties}
          />
        </S.PropertyGroup>
      </S.WorkoutExercise>
    )
    : (
      <Draggable
        index={index}
        draggableId={String(exercise.id)}
      >
        {provided => (
          <S.WorkoutExercise
            ref={provided.innerRef}
            {...provided.draggableProps}
            number={number}
            topLine={number === prevNumber}
            bottomLine={number === nextNumber}
          >
            <S.Meta>
              <S.Sort
                {...provided.dragHandleProps}
              >
                <Icon type="fas fa-arrows-alt-v" />
              </S.Sort>

              <ExerciseOrder
                number={number}
                numbers={numbers}
                exercise={exercise}
                onChange={onRequestSetOrderName}
              />

              <Dropdown
                trigger={(
                  <S.ActionsTrigger />
                )}
                items={actions}
              />

              <Flex column grow="1">
                <Flex>
                  <S.Name
                    {...provided.dragHandleProps}
                    to={`/workout_exercises/${exercise.id}/overview`}
                  >
                    {exercise.name}
                  </S.Name>
                  <Alternatives
                    exercise={exercise}
                    onAdd={onRequestAddAlternative}
                    onSet={onRequestSetAlternative}
                    onRemove={onRequestRemoveAlternative}
                  />
                </Flex>

                <S.Note
                  defaultValue={exercise.note || ''}
                  placeholder="Add Note (Exercise Tips, suggestions or exercise specifics)"
                  onSave={(note) => {
                    onRequestUpdate(
                      exercise.id,
                      {
                        note,
                      },
                    );
                  }}
                />
              </Flex>
            </S.Meta>
            <S.PropertyGroup>
              <ExerciseProperties
                exerciseId={exercise.id}
                properties={properties}
                dieRoll={exercise.hasDieRoll}
                onRequestDieRoll={() => onRequestDieRoll(exercise)}
                onRequestSave={onRequestSaveProperty}
                onRequestDelete={onRequestDeleteProperty}
              />

              {visibleSelect && (
                <Checkbox
                  id={String(exercise.id)}
                  isBlink
                  size="large"
                  checked={!!exercise.selected}
                  onChange={() => onSelect(exercise.id, !exercise.selected)}
                />
              )}

              {!visibleSelect && (
                <Flex align="center" noPrint>
                  <Tooltip
                    body={tooltipText.exerciseProperty}
                    positions={['left']}
                    width="150px"
                  >
                    <S.Add
                      onClick={() => {
                        onRequestAddProperty({
                          position: properties.length + 1,
                          workout_exercise_id: exercise.id,
                        });
                      }}
                    >
                      <S.PlusCircle />
                    </S.Add>
                  </Tooltip>
                </Flex>
              )}
            </S.PropertyGroup>
          </S.WorkoutExercise>
        )}
      </Draggable>
    );
};

export default WorkoutExercise;
